class CategoryCarousel extends HTMLElement {

  static get observedAttributes() {
    return [
      'data-loop', 'data-autoplay', 'data-speed',
      'data-center-width', 'data-gap', 'data-mobile-slider'
    ];
  }

  static get DESKTOP_BP() { return 990; }
  static get TABLET_BP() { return 750; }
  static get ANIM_DURATION() { return 560; }

  static get SLOT_OFFSETS() { return [0, 1, 2, 3, 4, 5]; }

  static get ALL_SLOT_CLASSES() {
    return [
      'center', 'side-left', 'side-right',
      'peek', 'peek-left', 'peek-right',
      'offscreen',
    ];
  }

  /* ── Lifecycle ─── */
  connectedCallback() {
    this._track = this.querySelector('.category-carousel');
    this._cards = Array.from(this.querySelectorAll('.category-carousel-card'));
    this._btnPrev = this.querySelector('.carousel-prev');
    this._btnNext = this.querySelector('.carousel-next');
    this._navEl = this.querySelector('.category-carousel-nav');

    if (!this._track || !this._cards.length) return;

    this._state = {
      headIndex: this._cards.length === 4 ? 0 : 1,
      total: this._cards.length,
      isAnimating: false,
      isDesktop: window.innerWidth >= CategoryCarousel.DESKTOP_BP,
      isTablet: window.innerWidth >= CategoryCarousel.TABLET_BP && window.innerWidth < CategoryCarousel.DESKTOP_BP,
      mobileSlider: this.dataset.mobileSlider !== 'false', // default true
      desktopHasSlider: this._cards.length >= 4,
    };

    this._autoplayTimer = null;
    this._resizeTimer = null;

    this._bindEvents();
    this._render(false);
    this._updateNavVisibility();
    this._updateNavButtons();

    if (this.dataset.autoplay === 'true' && this._shouldShowSlider()) {
      this._startAutoplay();
      this.addEventListener('mouseenter', () => this._stopAutoplay());
      this.addEventListener('mouseleave', () => this._startAutoplay());
    }
  }

  disconnectedCallback() {
    this._stopAutoplay();
    window.removeEventListener('resize', this._onResize);
  }

  attributeChangedCallback(name, oldVal, newVal) {
    if (!this._state || oldVal === newVal) return;

    if (name === 'data-autoplay') {
      newVal === 'true' ? this._startAutoplay() : this._stopAutoplay();
    }
    if (name === 'data-speed' && this._autoplayTimer) {
      this._stopAutoplay();
      this._startAutoplay();
    }
    if (name === 'data-center-width' || name === 'data-gap') {
      this._render(false);
    }
    if (name === 'data-mobile-slider') {
      this._state.mobileSlider = newVal !== 'false';
      this._render(false);
      this._updateNavVisibility();
    }
  }

  /* ── Public API ── */
  next(n = 1) { this._advance(n); }
  prev(n = 1) { this._advance(-n); }
  goTo(index) {
    if (!this._state) return;
    const delta = this._mod(index - this._state.headIndex, this._state.total);
    this._advance(delta <= this._state.total / 2 ? delta : delta - this._state.total);
  }

  /* ── Should this instance show a slider? ────────────────────── */
  _shouldShowSlider() {
    if (this._state.isDesktop) {
      return this._state.desktopHasSlider; // need 4+ slides on desktop
    } else {
      return this._state.mobileSlider; // respect schema setting
    }
  }

  /* ── Build dynamic slot positions ───────────────────────────── */
  _buildSlots() {
    // data-center-width = width of EACH center card (e.g. 27)
    const inner = parseFloat(this.dataset.centerWidth) || 27;
    const gap = parseFloat(this.dataset.gap) || 2;
    const peek = (100 - inner * 2 - gap * 3) / 2;

    const peekLeftStart = 0;
    const sideLeftStart = peek + gap;
    const sideRightStart = sideLeftStart + inner + gap;
    const peekRightStart = sideRightStart + inner + gap;

    const offLeftStart = -(peek + gap);
    const offRightStart = peekRightStart + peek + gap;

    return [
      { name: 'offLeft', left: offLeftStart, width: peek, classes: ['offscreen'] },
      { name: 'peekLeft', left: peekLeftStart, width: peek, classes: ['peek', 'peek-left'] },
      { name: 'sideLeft', left: sideLeftStart, width: inner, classes: ['center', 'side-left'] },
      { name: 'sideRight', left: sideRightStart, width: inner, classes: ['center', 'side-right'] },
      { name: 'peekRight', left: peekRightStart, width: peek, classes: ['peek', 'peek-right'] },
      { name: 'offRight', left: offRightStart, width: peek, classes: ['offscreen'] },
    ];
  }

  /* ── Helpers ───── */
  _mod(n, m) {
    return ((n % m) + m) % m;
  }

  _getCenterHeight() {
    const h = parseInt(this._track.style.height || '', 10);
    if (h > 0) return h;
    const computed = parseInt(getComputedStyle(this._track).height, 10);
    if (computed > 0) return computed;
    return 420;
  }

  /* ── Bind events ─ */
  _bindEvents() {
    if (this._btnPrev) {
      this._btnPrev.addEventListener('click', () => {
        this._stopAutoplay();
        this._advance(-1);
      });
    }
    if (this._btnNext) {
      this._btnNext.addEventListener('click', () => {
        this._stopAutoplay();
        this._advance(1);
      });
    }

    // this._cards.forEach((card, i) => {
    //   card.addEventListener('click', (e) => {
    //     // Only intercept clicks if slider is active
    //     if (!this._shouldShowSlider()) return;
    //     if (card.classList.contains('center')) return;
    //     e.preventDefault();

    //     const fwd = this._mod(i - this._state.headIndex, this._state.total);
    //     const bwd = this._mod(this._state.headIndex - i, this._state.total);
    //     this._advance(fwd <= bwd ? fwd : -bwd);
    //     this._stopAutoplay();
    //   });
    // });

    this._onResize = () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        const wasDesktop = this._state.isDesktop;
        const wasTablet = this._state.isTablet;
        this._state.isDesktop = window.innerWidth >= CategoryCarousel.DESKTOP_BP;
        this._state.isTablet = window.innerWidth >= CategoryCarousel.TABLET_BP && window.innerWidth < CategoryCarousel.DESKTOP_BP;

        if (wasDesktop !== this._state.isDesktop || wasTablet !== this._state.isTablet) {
          if (this._state.isDesktop) this._track.style.height = '';
          this._render(false);
          this._updateNavVisibility();
        }
      }, 120);
    };
    window.addEventListener('resize', this._onResize);
  }

  /* ── Render dispatcher ──────────────────────────────────────── */
  _render(animate) {
    if (this._state.isDesktop) {
      this._state.desktopHasSlider
        ? this._applyDesktopCarousel(animate)
        : this._applyDesktopStaticGrid();
    } else {
      this._state.mobileSlider
        ? this._applyMobileCarousel()
        : this._applyMobileStaticGrid();
    }
  }

  /* ── Desktop: 4-slot carousel ───────────────────────────────── */
  _applyDesktopCarousel(animate) {
    const { SLOT_OFFSETS, ALL_SLOT_CLASSES, ANIM_DURATION } = CategoryCarousel;
    const slots = this._buildSlots();

    if (!animate) this._track.classList.remove('is-animating');
    else this._track.classList.add('is-animating');

    // Remove static grid classes
    this._track.classList.remove('static-grid', 'static-grid--desktop');

    const slotForCard = new Map();
    slots.forEach((slot, i) => {
      const cardIdx = this._mod(this._state.headIndex + SLOT_OFFSETS[i+1], this._state.total);
      slotForCard.set(cardIdx, slot);
    });

    this._cards.forEach((card, cardIdx) => {
      const slot = slotForCard.get(cardIdx);

      card.classList.remove(...ALL_SLOT_CLASSES);
      card.style.height = '';
      card.style.position = '';
      card.style.top = '';

      if (slot) {
        slot.classes.forEach((cls) => card.classList.add(cls));
        card.style.left = slot.left + '%';
        card.style.width = slot.width + '%';
      } else {
        card.classList.add('offscreen');
        card.style.left = '102%';
        card.style.width = '20%';
      }
    });

    if (animate) {
      setTimeout(() => {
        this._track.classList.remove('is-animating');
        this._state.isAnimating = false;
        this._updateNavButtons();
      }, ANIM_DURATION);
    }
  }

  /* ── Desktop: static grid (< 4 slides) ──────────────────────── */
  _applyDesktopStaticGrid() {
    const { ALL_SLOT_CLASSES } = CategoryCarousel;

    this._track.classList.remove('is-animating');
    this._track.classList.add('static-grid', 'static-grid--desktop');

    this._cards.forEach((card) => {
      card.classList.remove(...ALL_SLOT_CLASSES);
      card.style.left = '';
      card.style.width = '';
      card.style.height = '';
      card.style.position = '';
      card.style.top = '';
    });
  }

  /* ── Mobile/Tablet: carousel (1 slide at a time) ────────────── */
  _applyMobileCarousel() {
    const { ALL_SLOT_CLASSES } = CategoryCarousel;
    const centerHeight = this._getCenterHeight();

    this._track.classList.remove('static-grid', 'static-grid--tablet', 'static-grid--mobile');

    this._cards.forEach((card, cardIdx) => {
      card.classList.remove(...ALL_SLOT_CLASSES);
      card.style.left = '';
      card.style.width = '';

      if (cardIdx === this._mod(this._state.headIndex, this._state.total)) {
        card.classList.add('center');
        card.style.position = 'relative';
        card.style.top = 'auto';
        card.style.height = centerHeight + 'px';
      } else {
        card.style.position = 'absolute';
        card.style.top = '0';
        card.style.height = centerHeight + 'px';
      }
    });

    this._track.style.height = centerHeight + 'px';
    this._updateNavButtons();
  }

  /* ── Mobile/Tablet: static grid ─────────────────────────────── */
  _applyMobileStaticGrid() {
    const { ALL_SLOT_CLASSES } = CategoryCarousel;

    this._track.classList.add('static-grid');
    if (this._state.isTablet) {
      this._track.classList.add('static-grid--tablet');
      this._track.classList.remove('static-grid--mobile');
    } else {
      this._track.classList.add('static-grid--mobile');
      this._track.classList.remove('static-grid--tablet');
    }

    this._cards.forEach((card) => {
      card.classList.remove(...ALL_SLOT_CLASSES);
      card.style.left = '';
      card.style.width = '';
      card.style.height = '';
      card.style.position = '';
      card.style.top = '';
    });

    this._track.style.height = '';
  }

  /* ── Nav visibility ─────────────────────────────────────────── */
  _updateNavVisibility() {
    if (!this._navEl) return;

    const showNav = this._shouldShowSlider() && (
      this._state.isDesktop
        ? this._state.total > 3
        : this._state.total > 1
    );

    this._navEl.style.display = showNav ? '' : 'none';
  }

  /* ── Nav button states ──────────────────────────────────────── */
  _updateNavButtons() {
    if (!this._btnPrev || !this._btnNext) return;
    if (!this._shouldShowSlider()) return;
    if (this.dataset.loop === 'true') return;

    if (this._state.isDesktop) {
      // For exactly 4 slides, loop is effectively always on (all slots filled)
      if (this._state.total >= 4) return;

      // For 5+ slides:
      // headIndex=1 shows [0,1,2,3], can't go back further without wrapping
      // headIndex=total-3 shows [...,N-3,N-2,N-1] with last card in peekRight
      this._btnPrev.classList.toggle('is-disabled', this._state.headIndex === 1);
      this._btnNext.classList.toggle('is-disabled', this._state.headIndex >= this._state.total - 3);
    } else {
      this._btnPrev.classList.toggle('is-disabled', this._state.headIndex === 0);
      this._btnNext.classList.toggle('is-disabled', this._state.headIndex >= this._state.total - 1);
    }
  }

  /* ── Advance ───── */
  _advance(delta) {
    if (!this._shouldShowSlider()) return;
    if (this._state.isAnimating) return;
    this._state.isAnimating = true;

    this._state.headIndex = this._mod(
      this._state.headIndex + delta,
      this._state.total
    );

    this.dispatchEvent(new CustomEvent('carousel:change', {
      bubbles: true,
      detail: { index: this._state.headIndex },
    }));

    if (this._state.isDesktop && this._state.desktopHasSlider) {
      this._applyDesktopCarousel(true);
    } else if (this._state.mobileSlider) {
      this._applyMobileCarousel();
      setTimeout(() => { this._state.isAnimating = false; }, 460);
    }

    this._updateNavButtons();
  }

  /* ── Autoplay ──── */
  _startAutoplay() {
    if (!this._shouldShowSlider()) return;
    if (this._autoplayTimer) return;
    const speed = parseInt(this.dataset.speed, 10) || 3;
    this._autoplayTimer = setInterval(() => this._advance(1), speed * 1000);
  }

  _stopAutoplay() {
    if (this._autoplayTimer) {
      clearInterval(this._autoplayTimer);
      this._autoplayTimer = null;
    }
  }
}

/* ── Register ──────── */
if (!customElements.get('category-carousel')) {
  customElements.define('category-carousel', CategoryCarousel);
}

/* ── Shopify theme editor live reload ───────────────────────────── */
document.addEventListener('shopify:section:load', (e) => {
  e.target.querySelectorAll('category-carousel').forEach((el) => {
    el.connectedCallback();
  });
});